import { useState } from "react";
import {
  Clock, CheckCircle, XCircle, Loader2, Eye,
  User, FileText, MapPin, Wrench, ExternalLink, MessageSquare, Calendar,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { AdminStatusBadge } from "@/admin/components/AdminStatusBadge";
import { ReasonModal } from "@/admin/components/ReasonModal";
import { useReports } from "@/admin/hooks/useReports";
import type { ReportStatus, AdminReportRow } from "@/admin/types/admin";
import { REPORT_STATUS_LABELS } from "@/admin/types/admin";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

// ─── Constantes ───────────────────────────────────────────────────────────────

const FILTERS: ReportStatus[] = ["open", "inProgress", "resolved", "dismissed"];
const FILTER_ALL = "all";

const REPORT_TYPE_LABELS: Record<string, string> = {
  user: "Utilisateur",
  ad: "Annonce",
  message: "Message",
  photo: "Photo",
};

const REASON_LABELS: Record<string, string> = {
  fake_profile: "Profil faux ou usurpation d'identité",
  scam: "Arnaque ou tentative d'escroquerie",
  inappropriate_behavior: "Comportement inapproprié ou agressif",
  spam: "Spam ou démarchage abusif",
  fake_ad: "Annonce fausse ou trompeuse",
  inappropriate_content: "Contenu inapproprié ou offensant",
  wrong_category: "Mauvaise catégorie ou hors-sujet",
  duplicate: "Annonce dupliquée",
  other: "Autre raison",
};

// ─── Panel de détail ──────────────────────────────────────────────────────────

function ReportDetailDialog({
  report,
  open,
  onClose,
  onMarkInProgress,
  onResolve,
  onDismiss,
  isPending,
}: {
  report: AdminReportRow | null;
  open: boolean;
  onClose: () => void;
  onMarkInProgress: () => void;
  onResolve: () => void;
  onDismiss: () => void;
  isPending: boolean;
}) {
  if (!report) return null;

  const reasonLabel = REASON_LABELS[report.reason] ?? report.reason;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            <span>Signalement</span>
            <AdminStatusBadge status={report.status} />
            <span className="ml-auto text-xs font-normal text-gray-400">
              {new Date(report.createdAt).toLocaleDateString("fr-FR", {
                day: "numeric", month: "long", year: "numeric",
                hour: "2-digit", minute: "2-digit",
              })}
            </span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 pt-1">

          {/* ── Signalé par ─────────────────────────────────────── */}
          <section className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-1">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2 flex items-center gap-1.5">
              <User className="h-3.5 w-3.5" />
              Signalé par
            </p>
            {report.reporterUserId ? (
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {report.reporterName ?? "Inconnu"}
                  </p>
                  <p className="text-xs text-gray-500 font-mono">{report.reporterUserId}</p>
                </div>
                <a
                  href={`/admin/users`}
                  className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                >
                  <ExternalLink className="h-3 w-3" />
                  Voir dans Utilisateurs
                </a>
              </div>
            ) : (
              <p className="text-sm text-gray-500">Signalement anonyme</p>
            )}
          </section>

          {/* ── Contenu signalé ─────────────────────────────────── */}
          {report.targetType === "ad" && (
            <section className="rounded-lg border border-blue-100 bg-blue-50/40 p-4 space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-blue-400 flex items-center gap-1.5">
                <FileText className="h-3.5 w-3.5" />
                Annonce signalée
              </p>

              {report.adTarget ? (
                <>
                  <div className="space-y-1">
                    <p className="text-base font-semibold text-gray-900">{report.adTarget.title}</p>

                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        {report.adTarget.commune}, {report.adTarget.wilaya}
                      </span>
                      <span className="flex items-center gap-1">
                        <Wrench className="h-3 w-3" />
                        {report.adTarget.metier}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        Publiée le {new Date(report.adTarget.createdAt).toLocaleDateString("fr-FR")}
                      </span>
                    </div>
                  </div>

                  {report.adTarget.description && (
                    <div className="rounded-md bg-white border border-blue-100 p-3">
                      <p className="text-xs font-medium text-gray-500 mb-1">Description</p>
                      <p className="text-sm text-gray-700 whitespace-pre-line leading-relaxed">
                        {report.adTarget.description}
                      </p>
                    </div>
                  )}

                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <p className="text-xs text-gray-500">Auteur de l'annonce</p>
                      <p className="text-sm font-medium text-gray-900">{report.adTarget.authorName}</p>
                      <p className="text-xs text-gray-400 font-mono">{report.adTarget.authorUserId}</p>
                    </div>
                    <a
                      href={`/annonce/${report.targetId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline border border-primary/30 rounded-md px-2.5 py-1.5"
                    >
                      <ExternalLink className="h-3 w-3" />
                      Voir l'annonce
                    </a>
                  </div>
                </>
              ) : (
                <p className="text-sm text-gray-400 italic">
                  Annonce introuvable (supprimée ou ID invalide) — ID : <span className="font-mono">{report.targetId}</span>
                </p>
              )}
            </section>
          )}

          {report.targetType === "user" && (
            <section className="rounded-lg border border-orange-100 bg-orange-50/40 p-4 space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-orange-400 flex items-center gap-1.5">
                <User className="h-3.5 w-3.5" />
                Utilisateur signalé
              </p>

              {report.userTarget ? (
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div className="space-y-0.5">
                    <p className="text-sm font-semibold text-gray-900">{report.userTarget.displayName}</p>
                    {report.userTarget.specialite && (
                      <p className="text-xs text-gray-500 flex items-center gap-1">
                        <Wrench className="h-3 w-3" />{report.userTarget.specialite}
                      </p>
                    )}
                    {report.userTarget.wilaya && (
                      <p className="text-xs text-gray-500 flex items-center gap-1">
                        <MapPin className="h-3 w-3" />{report.userTarget.wilaya}
                      </p>
                    )}
                    <p className="text-xs text-gray-400 font-mono">{report.userTarget.userId}</p>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <a
                      href={`/artisan/${report.targetId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline border border-primary/30 rounded-md px-2.5 py-1.5"
                    >
                      <ExternalLink className="h-3 w-3" />
                      Voir le profil
                    </a>
                    <a
                      href="/admin/users"
                      className="inline-flex items-center gap-1.5 text-xs text-gray-600 hover:underline border border-gray-200 rounded-md px-2.5 py-1.5"
                    >
                      <User className="h-3 w-3" />
                      Gérer dans Utilisateurs
                    </a>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-400 italic">
                  Utilisateur introuvable — ID : <span className="font-mono">{report.targetId}</span>
                </p>
              )}
            </section>
          )}

          {/* ── Motif & Détails ──────────────────────────────────── */}
          <section className="rounded-lg border border-gray-200 p-4 space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 flex items-center gap-1.5">
              <MessageSquare className="h-3.5 w-3.5" />
              Motif du signalement
            </p>
            <p className="text-sm font-medium text-gray-800">{reasonLabel}</p>
            {report.details && (
              <div className="rounded-md bg-gray-50 border border-gray-100 p-3 mt-1">
                <p className="text-xs text-gray-500 mb-1">Détails fournis par le signalant</p>
                <p className="text-sm text-gray-700 italic whitespace-pre-line">{report.details}</p>
              </div>
            )}
          </section>

          {/* ── Note de résolution si résolue ────────────────────── */}
          {report.resolutionNote && (
            <section className="rounded-lg border border-green-100 bg-green-50/40 p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-green-500 mb-1">Note de résolution</p>
              <p className="text-sm text-gray-700">{report.resolutionNote}</p>
            </section>
          )}

          {/* ── Actions ─────────────────────────────────────────── */}
          {(report.status === "open" || report.status === "inProgress") && (
            <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-gray-100">
              {report.status === "open" && (
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-1.5 text-blue-700 border-blue-300 hover:bg-blue-50"
                  disabled={isPending}
                  onClick={onMarkInProgress}
                >
                  <Clock className="h-3.5 w-3.5" />
                  Marquer en cours
                </Button>
              )}
              <Button
                size="sm"
                variant="outline"
                className="gap-1.5 text-green-700 border-green-300 hover:bg-green-50"
                disabled={isPending}
                onClick={onResolve}
              >
                <CheckCircle className="h-3.5 w-3.5" />
                Résoudre
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="gap-1.5 text-gray-600 border-gray-300 hover:bg-gray-50"
                disabled={isPending}
                onClick={onDismiss}
              >
                <XCircle className="h-3.5 w-3.5" />
                Classer sans suite
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Page principale ──────────────────────────────────────────────────────────

export default function AdminReports() {
  const [filter, setFilter] = useState<ReportStatus | typeof FILTER_ALL>("open");
  const [selectedReport, setSelectedReport] = useState<AdminReportRow | null>(null);
  const [resolveTarget, setResolveTarget] = useState<AdminReportRow | null>(null);
  const [dismissTarget, setDismissTarget] = useState<AdminReportRow | null>(null);

  const effectiveFilter = filter === FILTER_ALL ? undefined : filter;
  const { query, updateStatusMutation } = useReports(effectiveFilter);

  const reports = query.data ?? [];

  const handleResolve = (report: AdminReportRow) => {
    setSelectedReport(null);
    setResolveTarget(report);
  };

  const handleDismiss = (report: AdminReportRow) => {
    setSelectedReport(null);
    setDismissTarget(report);
  };

  const handleMarkInProgress = (report: AdminReportRow) => {
    updateStatusMutation.mutate({ reportId: report.id, newStatus: "inProgress" });
    setSelectedReport((prev) => prev ? { ...prev, status: "inProgress" } : prev);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Signalements</h1>
        <p className="text-sm text-gray-500 mt-1">{reports.length} signalement(s)</p>
      </div>

      {/* Filtres */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setFilter(FILTER_ALL)}
          className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
            filter === FILTER_ALL
              ? "bg-primary text-white"
              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
          }`}
        >
          Tous
        </button>
        {FILTERS.map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
              filter === f
                ? "bg-primary text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            {REPORT_STATUS_LABELS[f]}
          </button>
        ))}
      </div>

      {/* Table */}
      {query.isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : reports.length === 0 ? (
        <div className="rounded-xl border border-gray-200 bg-white p-12 text-center">
          <p className="text-gray-400">Aucun signalement trouvé</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-500">Type</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">Cible</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">Raison</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">Signalé par</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">Date</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">Statut</th>
                <th className="px-4 py-3 text-right font-medium text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {reports.map((report) => {
                const targetLabel =
                  report.targetType === "ad" && report.adTarget
                    ? report.adTarget.title
                    : report.targetType === "user" && report.userTarget
                    ? report.userTarget.displayName
                    : report.targetId.slice(0, 8) + "…";

                return (
                  <tr
                    key={report.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => setSelectedReport(report)}
                  >
                    <td className="px-4 py-3">
                      <span className="font-medium text-gray-900">
                        {REPORT_TYPE_LABELS[report.reportType] ?? report.reportType}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-700 max-w-[180px] truncate" title={targetLabel}>
                      {targetLabel}
                    </td>
                    <td className="px-4 py-3 text-gray-500 max-w-[160px] truncate">
                      {REASON_LABELS[report.reason] ?? report.reason}
                    </td>
                    <td className="px-4 py-3 text-gray-500">{report.reporterName ?? "Anonyme"}</td>
                    <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                      {new Date(report.createdAt).toLocaleDateString("fr-FR")}
                    </td>
                    <td className="px-4 py-3">
                      <AdminStatusBadge status={report.status} />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                        <Button
                          size="sm"
                          variant="outline"
                          className="gap-1 text-gray-700"
                          onClick={() => setSelectedReport(report)}
                        >
                          <Eye className="h-3.5 w-3.5" />
                          Détail
                        </Button>
                        {report.status === "open" && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="gap-1 text-blue-700 border-blue-300 hover:bg-blue-50"
                            disabled={updateStatusMutation.isPending}
                            onClick={() =>
                              updateStatusMutation.mutate({
                                reportId: report.id,
                                newStatus: "inProgress",
                              })
                            }
                          >
                            <Clock className="h-3.5 w-3.5" />
                            En cours
                          </Button>
                        )}
                        {(report.status === "open" || report.status === "inProgress") && (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              className="gap-1 text-green-700 border-green-300 hover:bg-green-50"
                              onClick={() => handleResolve(report)}
                            >
                              <CheckCircle className="h-3.5 w-3.5" />
                              Résoudre
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="gap-1 text-gray-600 border-gray-300 hover:bg-gray-50"
                              onClick={() => handleDismiss(report)}
                            >
                              <XCircle className="h-3.5 w-3.5" />
                              Classer
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Panel de détail */}
      <ReportDetailDialog
        report={selectedReport}
        open={!!selectedReport}
        onClose={() => setSelectedReport(null)}
        onMarkInProgress={() => selectedReport && handleMarkInProgress(selectedReport)}
        onResolve={() => selectedReport && handleResolve(selectedReport)}
        onDismiss={() => selectedReport && handleDismiss(selectedReport)}
        isPending={updateStatusMutation.isPending}
      />

      {/* Modal résolution */}
      <ReasonModal
        open={!!resolveTarget}
        title="Résoudre le signalement"
        reasonCategory="report_resolution"
        loading={updateStatusMutation.isPending}
        onConfirm={(reasonCode, notes) => {
          if (!resolveTarget) return;
          updateStatusMutation.mutate(
            { reportId: resolveTarget.id, newStatus: "resolved", reasonCode, notes },
            { onSuccess: () => setResolveTarget(null) }
          );
        }}
        onCancel={() => setResolveTarget(null)}
      />

      {/* Modal classement sans suite */}
      <ReasonModal
        open={!!dismissTarget}
        title="Classer le signalement sans suite"
        reasonCategory="report_resolution"
        loading={updateStatusMutation.isPending}
        onConfirm={(reasonCode, notes) => {
          if (!dismissTarget) return;
          updateStatusMutation.mutate(
            { reportId: dismissTarget.id, newStatus: "dismissed", reasonCode, notes },
            { onSuccess: () => setDismissTarget(null) }
          );
        }}
        onCancel={() => setDismissTarget(null)}
      />
    </div>
  );
}
